import { useState } from "react";
import { CheckCircle2, Loader2, Search, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import type { ImportErrorKind } from "../hooks/useImportDeal";
import type { ImportMissingField } from "../types";

type ImportStatus = "idle" | "loading" | "success" | "error";

const ERROR_MESSAGES: Record<ImportErrorKind, string> = {
  not_found: "Deal não encontrado. Verifique o ID e tente novamente.",
  server: "Erro ao importar o deal. Tente novamente em instantes.",
};

// Analytics tracks the canonical keys; only this component knows the copy.
const MISSING_FIELD_LABELS: Record<ImportMissingField, string> = {
  company: "empresa",
  city: "cidade",
  capacity: "capacidade",
  contact_email: "email do contato",
  contact_name: "nome do contato",
  segment: "segmento",
};

function formatFieldList(fields: ImportMissingField[]): string {
  const labels = fields.map((field) => MISSING_FIELD_LABELS[field]);
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} e ${labels[labels.length - 1]}`;
}

interface HubSpotImportCardProps {
  dealId: string;
  importStatus: ImportStatus;
  errorKind?: ImportErrorKind | null;
  missingFields?: ImportMissingField[];
  onDealIdChange: (value: string) => void;
  onImport: () => void;
  isEditMode?: boolean;
}

export function HubSpotImportCard({
  dealId,
  importStatus,
  errorKind = null,
  missingFields = [],
  onDealIdChange,
  onImport,
  isEditMode = false,
}: HubSpotImportCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  function handleImportClick() {
    if (isEditMode) {
      setConfirmOpen(true);
    } else {
      onImport();
    }
  }

  return (
    <div
      className="rounded-2xl p-5 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(145deg, #FAF5FF 0%, #F5F3FF 50%, #EDE9FE 100%)",
        border: "1.5px solid rgba(167,139,250,0.3)",
      }}
    >
      {importStatus === "loading" && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(124,58,237,0.08) 50%, transparent 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s ease-in-out infinite",
          }}
        />
      )}

      <div className="flex items-center gap-2.5 mb-4">
        <div
          className="w-8 h-8 rounded-xl flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #FF7A59, #FF957A)" }}
        >
          <span className="text-xs font-black text-white">H</span>
        </div>
        <div>
          <p className="text-sm font-bold" style={{ color: "#1A1814" }}>
            HubSpot CRM
          </p>
          <p className="text-[10px] font-medium" style={{ color: "#7C3AED" }}>
            Importação inteligente
          </p>
        </div>
        {importStatus === "success" && (
          <div
            className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{
              background: "#ECFDF5",
              border: "1px solid rgba(34,197,94,0.2)",
            }}
          >
            <CheckCircle2 className="w-3 h-3" style={{ color: "#22C55E" }} />
            <span
              className="text-[10px] font-semibold"
              style={{ color: "#22C55E" }}
            >
              Sincronizado
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            type="text"
            value={dealId}
            onChange={(e) => onDealIdChange(e.target.value)}
            placeholder="Deal ID *"
            disabled={importStatus === "loading"}
            onKeyDown={(e) => e.key === "Enter" && handleImportClick()}
            className="!bg-white/80 !text-sm"
            style={{
              borderColor:
                importStatus === "error"
                  ? "rgba(239,68,68,0.5)"
                  : "rgba(167,139,250,0.2)",
            }}
          />
        </div>
        <button
          type="button"
          onClick={handleImportClick}
          disabled={!dealId.trim() || importStatus === "loading"}
          className="flex items-center gap-2 px-5 py-2.5 rounded-[14px] text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background:
              importStatus === "loading"
                ? "linear-gradient(135deg, #A78BFA, #7C3AED)"
                : "linear-gradient(135deg, #7C3AED, #6D28D9)",
            color: "white",
            boxShadow: "0 4px 14px rgba(124,58,237,0.3)",
          }}
        >
          {importStatus === "loading" ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
          {importStatus === "loading"
            ? "Buscando..."
            : isEditMode
              ? "Atualizar dados"
              : "Importar"}
        </button>
      </div>

      {importStatus === "error" && (
        <p
          className="mt-3 text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2"
          style={{ animation: "aiStepEnter 0.3s ease" }}
        >
          {ERROR_MESSAGES[errorKind ?? "server"]}
        </p>
      )}

      {importStatus === "success" && missingFields.length > 0 && (
        <p
          className="mt-3 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-xl px-3 py-2"
          style={{ animation: "aiStepEnter 0.3s ease" }}
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Deal importado, mas sem {formatFieldList(missingFields)} no HubSpot
            — preencha manualmente.
          </span>
        </p>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm" showCloseButton={false}>
          <div className="flex flex-col items-center text-center gap-3 py-2">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: "#FFFBEB" }}
            >
              <AlertTriangle className="w-6 h-6" style={{ color: "#D97706" }} />
            </div>
            <DialogTitle
              className="text-base font-semibold"
              style={{ color: "#1A1814" }}
            >
              Substituir dados do diagnóstico?
            </DialogTitle>
            <DialogDescription className="text-sm" style={{ color: "#78736D" }}>
              Os dados do diagnóstico serão sobrescritos com as informações do
              HubSpot. Esta ação não pode ser desfeita.
            </DialogDescription>
            <div className="flex gap-2 w-full mt-1">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-[#ECEAE6]"
                style={{ background: "#F0EEEB", color: "#43403D" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmOpen(false);
                  onImport();
                }}
                className="flex-1 py-2 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
                style={{ background: "#1A1814", color: "white" }}
              >
                Sim, atualizar
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
